/**
 * SecureEmbed Monitor
 *
 * Live performance monitoring dashboard with real-time metrics
 * - Component cards grouped by Terraform layer
 * - [i] popovers for metric reference on each card
 * - Quick Actions section below cards
 * - Pipeline stage summary indicator
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Activity, RefreshCw, Clock,
  AlertTriangle, CheckCircle2, XCircle, Zap, Database,
  Globe, Server, HardDrive, BarChart3,
  ChevronDown, ChevronUp, Loader2, Wifi, Users, Box, Info
} from 'lucide-react';
import { metricsApi, ENV_COLORS, type HealthResponse, type ComponentHealth } from '../services/api';
import QuickActions from '../components/QuickActions';

// Time period options
const TIME_PERIODS = [
  { id: 'live', label: 'Live', duration: '5m', refresh: 10000 },
  { id: '1h', label: '1 Hour', duration: '1h', refresh: 30000 },
  { id: '6h', label: '6 Hours', duration: '6h', refresh: 60000 },
  { id: '24h', label: '24 Hours', duration: '24h', refresh: 120000 },
  { id: '7d', label: '7 Days', duration: '7d', refresh: 300000 },
  { id: '30d', label: '30 Days', duration: '30d', refresh: 600000 },
];

const ENVIRONMENTS = ['dev', 'test', 'uat', 'prod'];

const NO_THRESHOLD = -1;

interface MetricCard {
  id: string;
  name: string;
  icon: React.ElementType;
  getValue: (data: ComponentHealth) => string | number | null | undefined;
  getUnit: () => string;
  getThresholds: () => { warning: number; critical: number };
  seriesKey?: string;
}

const Sparkline: React.FC<{ data: number[]; color: string }> = ({ data, color }) => {
  if (data.length < 2) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const height = 28;
  const width = 90;
  const points = data.map((value, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((value - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const COMPONENT_METRICS: Record<string, MetricCard[]> = {
  api_gateway: [
    { id: 'latency', name: 'P99 Latency', icon: Clock, getValue: (d) => d.latency_p99, getUnit: () => 'ms', getThresholds: () => ({ warning: 500, critical: 1000 }), seriesKey: 'latency_p99_series' },
    { id: 'errors', name: '5XX Rate', icon: AlertTriangle, getValue: (d) => d['5xx_rate'], getUnit: () => '%', getThresholds: () => ({ warning: 1, critical: 5 }), seriesKey: '5xx_rate_series' },
  ],
  lambda: [
    { id: 'invocations', name: 'Invocations', icon: Zap, getValue: (d) => d.invocations, getUnit: () => '', getThresholds: () => ({ warning: NO_THRESHOLD, critical: NO_THRESHOLD }), seriesKey: 'invocations_series' },
    { id: 'errors', name: 'Error Rate', icon: AlertTriangle, getValue: (d) => d.error_rate, getUnit: () => '%', getThresholds: () => ({ warning: 1, critical: 5 }), seriesKey: 'error_rate_series' },
    { id: 'duration', name: 'Avg Duration', icon: Clock, getValue: (d) => d.duration_avg, getUnit: () => 'ms', getThresholds: () => ({ warning: 3000, critical: 10000 }), seriesKey: 'duration_avg_series' },
  ],
  opensearch: [
    { id: 'cpu', name: 'CPU', icon: Activity, getValue: (d) => d.cpu, getUnit: () => '%', getThresholds: () => ({ warning: 70, critical: 85 }), seriesKey: 'cpu_series' },
    { id: 'memory', name: 'JVM Memory', icon: Database, getValue: (d) => d.memory, getUnit: () => '%', getThresholds: () => ({ warning: 75, critical: 90 }), seriesKey: 'memory_series' },
  ],
  aurora: [
    { id: 'cpu', name: 'CPU', icon: Activity, getValue: (d) => d.cpu, getUnit: () => '%', getThresholds: () => ({ warning: 70, critical: 85 }), seriesKey: 'cpu_series' },
    { id: 'connections', name: 'Connections', icon: Users, getValue: (d) => d.connections, getUnit: () => '', getThresholds: () => ({ warning: 80, critical: 95 }), seriesKey: 'connections_series' },
  ],
  step_functions: [
    { id: 'success_rate', name: 'Success Rate', icon: CheckCircle2, getValue: (d) => d.success_rate, getUnit: () => '%', getThresholds: () => ({ warning: 95, critical: 90 }), seriesKey: 'success_rate_series' },
    { id: 'executions', name: 'Executions (24h)', icon: Activity, getValue: (d) => d.executions_succeeded, getUnit: () => '', getThresholds: () => ({ warning: NO_THRESHOLD, critical: NO_THRESHOLD }), seriesKey: 'executions_succeeded_series' },
  ],
  eks: [
    { id: 'nodes', name: 'Node Count', icon: Box, getValue: (d) => d.node_count, getUnit: () => '', getThresholds: () => ({ warning: NO_THRESHOLD, critical: NO_THRESHOLD }), seriesKey: 'node_count_series' },
    { id: 'cpu', name: 'CPU Utilization', icon: Activity, getValue: (d) => d.cpu_utilization, getUnit: () => '%', getThresholds: () => ({ warning: 70, critical: 85 }), seriesKey: 'cpu_utilization_series' },
  ],
  cognito: [
    { id: 'deployed', name: 'Deployed', icon: Users, getValue: (d) => d.deployed ? 1 : 0, getUnit: () => '', getThresholds: () => ({ warning: NO_THRESHOLD, critical: NO_THRESHOLD }) },
  ],
  s3: [
    { id: 'buckets', name: 'Buckets', icon: Server, getValue: (d) => d.total_buckets, getUnit: () => '', getThresholds: () => ({ warning: NO_THRESHOLD, critical: NO_THRESHOLD }) },
    { id: 'objects', name: 'Objects', icon: Database, getValue: (d) => d.total_objects, getUnit: () => '', getThresholds: () => ({ warning: NO_THRESHOLD, critical: NO_THRESHOLD }) },
  ],
  dynamodb: [
    { id: 'tables', name: 'Tables', icon: Database, getValue: (d) => d.total_tables, getUnit: () => '', getThresholds: () => ({ warning: NO_THRESHOLD, critical: NO_THRESHOLD }) },
    { id: 'items', name: 'Items', icon: Activity, getValue: (d) => d.total_items, getUnit: () => '', getThresholds: () => ({ warning: NO_THRESHOLD, critical: NO_THRESHOLD }) },
  ],
  cloudfront: [
    { id: 'distributions', name: 'Distributions', icon: Globe, getValue: (d) => d.total_distributions, getUnit: () => '', getThresholds: () => ({ warning: NO_THRESHOLD, critical: NO_THRESHOLD }) },
    { id: '5xx_rate', name: '5XX Rate', icon: AlertTriangle, getValue: (d) => d.error_rate_5xx, getUnit: () => '%', getThresholds: () => ({ warning: 0.5, critical: 1 }) },
  ],
};

// Layer grouping for components (bottom-up display)
interface LayerGroup {
  layer: string;
  name: string;
  components: { id: string; name: string; icon: React.ElementType; description: string }[];
}

const LAYER_GROUPS: LayerGroup[] = [
  {
    layer: '08a',
    name: 'Portal Hosting',
    components: [
      { id: 'cloudfront', name: 'CloudFront', icon: Globe, description: 'CDN and content delivery' },
    ],
  },
  {
    layer: '07a',
    name: 'Data Pipelines',
    components: [
      { id: 'step_functions', name: 'Step Functions', icon: Activity, description: 'Workflow orchestration' },
    ],
  },
  {
    layer: '05',
    name: 'Application',
    components: [
      { id: 'api_gateway', name: 'API Gateway', icon: Globe, description: 'REST API endpoints' },
    ],
  },
  {
    layer: '04',
    name: 'Compute',
    components: [
      { id: 'lambda', name: 'Lambda', icon: Zap, description: 'Serverless functions' },
      { id: 'eks', name: 'EKS Cluster', icon: Box, description: 'Kubernetes (optional)' },
    ],
  },
  {
    layer: '03',
    name: 'Data',
    components: [
      { id: 'opensearch', name: 'OpenSearch', icon: Database, description: 'Vector search engine' },
      { id: 'aurora', name: 'Aurora PostgreSQL', icon: HardDrive, description: 'Relational database' },
      { id: 'dynamodb', name: 'DynamoDB', icon: Database, description: 'NoSQL database tables' },
      { id: 's3', name: 'S3 Storage', icon: Server, description: 'Object storage buckets' },
    ],
  },
  {
    layer: '02',
    name: 'Security',
    components: [
      { id: 'cognito', name: 'Cognito', icon: Users, description: 'User authentication' },
    ],
  },
];

// Metric explanations for [i] popovers
const METRIC_EXPLANATIONS: Record<string, { name: string; desc: string; warn: string; crit: string }[]> = {
  api_gateway: [
    { name: 'P99 Latency', desc: '99th percentile response time. Under 500ms is healthy.', warn: '500ms', crit: '1000ms' },
    { name: '5XX Rate', desc: 'Server error percentage. 0% is ideal; above 1% needs investigation.', warn: '1%', crit: '5%' },
  ],
  lambda: [
    { name: 'Invocations', desc: 'Total function invocations in the period.', warn: 'N/A', crit: 'N/A' },
    { name: 'Error Rate', desc: 'Percentage of invocations resulting in errors or timeouts.', warn: '1%', crit: '5%' },
    { name: 'Avg Duration', desc: 'Average execution time. High values may indicate cold starts.', warn: '3000ms', crit: '10000ms' },
  ],
  opensearch: [
    { name: 'CPU', desc: 'Average CPU utilisation across data nodes.', warn: '70%', crit: '85%' },
    { name: 'JVM Memory', desc: 'Heap memory pressure. Above 85% risks GC pauses.', warn: '75%', crit: '90%' },
  ],
  aurora: [
    { name: 'CPU', desc: 'Database CPU utilisation.', warn: '70%', crit: '85%' },
    { name: 'Connections', desc: 'Active database connections.', warn: '80', crit: '95' },
  ],
  step_functions: [
    { name: 'Success Rate', desc: 'Percentage of executions completing successfully.', warn: '95%', crit: '90%' },
    { name: 'Executions', desc: 'Number of workflow executions in the period.', warn: 'N/A', crit: 'N/A' },
  ],
  eks: [
    { name: 'Node Count', desc: 'Number of active Kubernetes nodes.', warn: 'N/A', crit: 'N/A' },
    { name: 'CPU Utilization', desc: 'Average CPU across cluster nodes.', warn: '70%', crit: '85%' },
  ],
  cognito: [
    { name: 'Deployed', desc: 'Whether the user pool exists.', warn: 'N/A', crit: 'N/A' },
  ],
  s3: [
    { name: 'Buckets', desc: 'Number of S3 buckets matching the environment prefix.', warn: 'N/A', crit: 'N/A' },
    { name: 'Objects', desc: 'Total object count across buckets.', warn: 'N/A', crit: 'N/A' },
  ],
  dynamodb: [
    { name: 'Tables', desc: 'Number of DynamoDB tables.', warn: 'N/A', crit: 'N/A' },
    { name: 'Items', desc: 'Total item count.', warn: 'N/A', crit: 'N/A' },
  ],
  cloudfront: [
    { name: 'Distributions', desc: 'Number of CloudFront distributions.', warn: 'N/A', crit: 'N/A' },
    { name: '5XX Rate', desc: 'Average 5XX error rate.', warn: '0.5%', crit: '1%' },
  ],
};


// [i] Info Popover component
const MetricInfoPopover: React.FC<{ componentId: string }> = ({ componentId }) => {
  const [show, setShow] = useState(false);
  const metrics = METRIC_EXPLANATIONS[componentId];
  if (!metrics) return null;

  return (
    <div className="relative">
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShow(!show); }}
        className="p-1 rounded hover:bg-gray-200 transition-colors"
        title="Metric reference"
      >
        <Info className="w-4 h-4 text-gray-400" />
      </button>
      {show && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShow(false)} />
          <div className="absolute right-0 top-8 z-50 w-72 bg-white rounded-xl shadow-xl border border-gray-200 p-3">
            <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Metric Reference</h4>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-400 border-b border-gray-100">
                  <th className="text-left pb-1 font-medium">Metric</th>
                  <th className="text-left pb-1 font-medium">Warn</th>
                  <th className="text-left pb-1 font-medium">Crit</th>
                </tr>
              </thead>
              <tbody>
                {metrics.map(m => (
                  <tr key={m.name} className="border-b border-gray-50 last:border-0">
                    <td className="py-1.5">
                      <div className="font-medium text-gray-700">{m.name}</div>
                      <div className="text-gray-400 leading-snug">{m.desc}</div>
                    </td>
                    <td className="py-1.5 text-yellow-600 font-medium">{m.warn}</td>
                    <td className="py-1.5 text-red-600 font-medium">{m.crit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

const Monitor: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [environment, setEnvironment] = useState(searchParams.get('env') || 'dev');
  const [timePeriod] = useState(TIME_PERIODS[0]);
  const [healthData, setHealthData] = useState<HealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [actionsExpanded, setActionsExpanded] = useState(false);
  const envColors = ENV_COLORS[environment as keyof typeof ENV_COLORS];
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchHealth = useCallback(async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const data = await metricsApi.getHealth(environment, undefined, {
        duration: timePeriod.duration,
        includeSeries: true,
      });
      if (!controller.signal.aborted) {
        setHealthData(data);
        setLastUpdate(new Date());
      }
    } catch (error) {
      if (!controller.signal.aborted) console.warn('Health fetch error:', error);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [environment, timePeriod.duration]);

  useEffect(() => {
    fetchHealth();
    const interval = autoRefresh ? setInterval(fetchHealth, timePeriod.refresh) : null;
    return () => {
      if (interval) clearInterval(interval);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [fetchHealth, timePeriod, autoRefresh, environment]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'critical': return 'text-red-600';
      case 'no_data': return 'text-gray-400';
      default: return 'text-gray-500';
    }
  };

  const getStatusBg = (status: string) => {
    switch (status) {
      case 'healthy': return 'bg-green-50 border-green-200';
      case 'warning': return 'bg-yellow-50 border-yellow-200';
      case 'critical': return 'bg-red-50 border-red-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'critical': return <XCircle className="w-5 h-5 text-red-500" />;
      default: return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getMetricStatus = (value: number | null, thresholds: { warning: number; critical: number }) => {
    if (value === null || thresholds.warning === NO_THRESHOLD) return 'healthy';
    if (value >= thresholds.critical) return 'critical';
    if (value >= thresholds.warning) return 'warning';
    return 'healthy';
  };

  const formatValue = (value: string | number | null | undefined, unit: string): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'number') {
      if (unit === '%') return `${value.toFixed(1)}%`;
      if (unit === 'ms') return `${value.toFixed(0)}ms`;
      return value.toLocaleString();
    }
    return String(value);
  };

  const getSparklineData = (
    series: Array<{ timestamp: string; value: number | null }> | undefined,
    fallbackValue: number | null
  ) => {
    const values = (series || []).map(point => point.value).filter((v): v is number => typeof v === 'number');
    if (values.length >= 2) return { data: values, hasRecentData: true };
    if (values.length === 1) return { data: [values[0], values[0]], hasRecentData: true };
    if (typeof fallbackValue === 'number') return { data: [fallbackValue, fallbackValue], hasRecentData: false };
    return { data: [0, 0], hasRecentData: false };
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-2xl shadow-sm border p-6" style={{ backgroundColor: envColors.bg, borderColor: envColors.primary }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#0369A1] flex items-center justify-center shadow-lg">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Performance Monitor</h1>
              <p className="text-gray-500 text-sm">Real-time Infrastructure Metrics</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <select
                value={environment}
                onChange={(e) => setEnvironment(e.target.value)}
                className="appearance-none bg-gray-100 border border-gray-200 rounded-lg px-4 py-2 pr-10 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#0369A1] shadow-sm"
              >
                {ENVIRONMENTS.map(env => (
                  <option key={env} value={env}>{env.toUpperCase()}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-gray-300 text-[#0369A1] focus:ring-[#0369A1]"
              />
              <span className="text-gray-600">Auto-refresh</span>
            </label>
            <button
              onClick={fetchHealth}
              disabled={loading}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Overall Health Banner */}
      <div className={`rounded-xl p-6 border ${getStatusBg(healthData?.overall_status || 'unknown')}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {loading ? <Loader2 className="w-8 h-8 animate-spin text-gray-400" /> : getStatusIcon(healthData?.overall_status || 'unknown')}
            <div>
              <h2 className="text-xl font-bold capitalize text-gray-900">
                {environment.toUpperCase()} Environment{healthData?.overall_status === 'unknown' ? ' — No Data' : `: ${healthData?.overall_status || 'Loading...'}`}
              </h2>
              <p className="text-gray-500">
                Last updated: {lastUpdate.toLocaleTimeString()} | Period: {timePeriod.label}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Wifi className={`w-5 h-5 ${autoRefresh ? 'text-green-500' : 'text-gray-400'}`} />
            <span className="text-sm text-gray-500">
              {autoRefresh ? `Every ${timePeriod.refresh / 1000}s` : 'Manual'}
            </span>
          </div>
        </div>
      </div>

      {/* Component Cards grouped by Terraform Layer */}
      <div className="space-y-6">
        {LAYER_GROUPS.map(group => {
          // Check if any component in this layer has active data
          const hasActiveComponents = group.components.some(c => {
            const data = healthData?.components?.[c.id as keyof typeof healthData.components] as ComponentHealth | undefined;
            const cStatus = data?.status || 'unknown';
            return cStatus !== 'unknown' && cStatus !== 'not_found' && cStatus !== 'no_data';
          });

          // Collapse empty layers into a single line
          if (!hasActiveComponents) {
            return (
              <div key={group.layer} className="flex items-center gap-3">
                <span className="text-xs font-bold text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Layer {group.layer} — {group.name}
                </span>
                <div className="flex-1 h-px bg-gray-100" />
                <span className="text-xs text-gray-300">No active components</span>
              </div>
            );
          }

          return (
          <div key={group.layer}>
            {/* Layer divider */}
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider whitespace-nowrap">
                Layer {group.layer} — {group.name}
              </span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {group.components.map(component => {
                const data = healthData?.components?.[component.id as keyof typeof healthData.components] as ComponentHealth | undefined;
                const metrics = COMPONENT_METRICS[component.id] || [];
                const Icon = component.icon;
                const cStatus = data?.status || 'unknown';
                const isNoData = cStatus === 'unknown' || cStatus === 'not_found' || cStatus === 'no_data';

                return (
                  <Link
                    to={`/services/${component.id}`}
                    key={component.id}
                    className={`rounded-xl border overflow-hidden transition-all hover:shadow-md cursor-pointer block ${getStatusBg(cStatus)}`}
                  >
                    {/* Header */}
                    <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          cStatus === 'healthy' ? 'bg-green-100' :
                          cStatus === 'warning' ? 'bg-yellow-100' :
                          cStatus === 'critical' ? 'bg-red-100' : 'bg-gray-100'
                        }`}>
                          <Icon className={`w-5 h-5 ${getStatusColor(cStatus)}`} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{component.name}</h3>
                          <p className="text-xs text-gray-500">{component.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <MetricInfoPopover componentId={component.id} />
                        {getStatusIcon(cStatus)}
                      </div>
                    </div>

                    {/* Metrics */}
                    {isNoData ? (
                      <div className="p-4 flex items-center gap-3 text-gray-400">
                        <Info className="w-5 h-5 flex-shrink-0" />
                        <p className="text-sm">No CloudWatch data — service idle, paused, or not deployed.</p>
                      </div>
                    ) : (
                      <div className="p-4 grid grid-cols-2 gap-4">
                        {metrics.map(metric => {
                          const value = data ? metric.getValue(data) : null;
                          const unit = metric.getUnit();
                          const thresholds = metric.getThresholds();
                          const mStatus = getMetricStatus(typeof value === 'number' ? value : null, thresholds);
                          const MetricIcon = metric.icon;
                          const series = metric.seriesKey && data ? ((data as any)[metric.seriesKey] as Array<{ timestamp: string; value: number | null }> | undefined) : undefined;
                          const sparkline = getSparklineData(series, typeof value === 'number' ? value : null);

                          return (
                            <div key={metric.id} className="bg-gray-50 rounded-lg p-2 border border-gray-100">
                              <div className="flex items-center gap-2 mb-1">
                                <MetricIcon className={`w-4 h-4 ${getStatusColor(mStatus)}`} />
                                <span className="text-xs text-gray-500">{metric.name}</span>
                              </div>
                              <div className={`text-lg font-bold ${getStatusColor(mStatus)}`}>
                                {formatValue(value, unit)}
                              </div>
                              <div className="mt-1">
                                <Sparkline data={sparkline.data} color={envColors.text} />
                                {!sparkline.hasRecentData && <div className="mt-1 text-[10px] text-gray-400">No recent data</div>}
                              </div>
                              {thresholds.warning > 0 && (
                                <div className="text-xs text-gray-400 mt-1">
                                  Warn: {thresholds.warning}{unit} | Crit: {thresholds.critical}{unit}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {metrics.length === 0 && (
                          <div className="col-span-2 text-center text-gray-400 py-4">No metrics available</div>
                        )}
                      </div>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
          );
        })}
      </div>

      {/* Quick Actions — collapsible */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <button
          onClick={() => setActionsExpanded(prev => !prev)}
          className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors"
        >
          <h3 className="text-sm font-semibold text-gray-700">Quick Actions</h3>
          {actionsExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </button>
        {actionsExpanded && (
          <div className="border-t border-gray-100">
            <QuickActions environment={environment} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Monitor;
